import { flexRender } from "@tanstack/react-table";
import type { Header, RowData, SortDirection } from "@tanstack/table-core";
import { Icon } from "../../../primitives/icon";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { cn } from "../../../utils/classes";
import { getStickyClasses } from "../utils/sticky";

export const HeaderSortIndicator = ({
  className,
  isSorted,
}: {
  className?: string;
  isSorted: false | SortDirection;
}) => {
  return (
    <div className={className}>
      <Icon
        type="ArrowUp"
        className={cn(
          "transition-transform duration-75 text-black -scale-y-100",
          isSorted === false &&
            "invisible opacity-0 group-hover/header-cell:visible group-hover/header-cell:opacity-20",
          isSorted === "asc" && "scale-y-100",
          "size-3.5",
        )}
      />
    </div>
  );
};

interface TableHeadCellProps<D extends RowData> {
  header: Header<D, unknown>;
}

export function TableHeadCell<D extends RowData>({
  header,
}: TableHeadCellProps<D>) {
  const sortable = header.column.getCanSort();
  const filter = header.column.columnDef.meta?.filter;
  const selectedFilterValues =
    (header.column.getFilterValue() as string[] | undefined) ?? [];

  const align =
    typeof header.column.columnDef.meta?.align === "function"
      ? header.column.columnDef.meta.align(header.getContext())
      : header.column.columnDef.meta?.align;

  const toggleFilterValue = (nextValue: string) => {
    const nextSelectedValues = selectedFilterValues.includes(nextValue)
      ? selectedFilterValues.filter((value) => value !== nextValue)
      : [...selectedFilterValues, nextValue];

    header.column.setFilterValue(
      nextSelectedValues.length > 0 ? nextSelectedValues : undefined,
    );
  };

  const filterLabel = (() => {
    if (!filter || filter.type !== "multi-select") return null;
    if (selectedFilterValues.length === 0) return "All";
    if (selectedFilterValues.length === 1) {
      return filter.options.find(
        (option) => option.value === selectedFilterValues[0],
      )?.label;
    }
    return `${selectedFilterValues.length} selected`;
  })();

  return (
    <th
      key={header.id}
      data-column={header.column.id}
      className={cn(
        "h-full mt-auto flex items-center gap-1 py-2.5 group/header-cell",
        getStickyClasses(header.column),
        {
          "cursor-pointer select-none": sortable,
          "justify-start": align === "left",
          "justify-center": align === "center",
          "justify-end": align === "right",
        },
        header.column.columnDef.meta?.headerCellClass,
      )}
      onClick={header.column.getToggleSortingHandler()}
    >
      {header.column.columnDef.meta?.rawHeader ? (
        // Full-height, non-clipping wrapper. The negative vertical margin eats
        // the th's py-2.5 so a header can draw edge-to-edge affordances.
        <div className="-my-2.5 flex h-full w-full items-stretch self-stretch">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      ) : (
        <span className="truncate">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
      )}
      {sortable && (
        <HeaderSortIndicator isSorted={header.column.getIsSorted()} />
      )}
      {filter?.type === "multi-select" && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "ml-auto relative inline-flex size-7 shrink-0 items-center justify-center rounded-sm border",
                selectedFilterValues.length > 0
                  ? "border-gray-300 bg-gray-100 text-gray-900"
                  : "border-transparent bg-transparent text-gray-500 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700",
              )}
              onClick={(event) => event.stopPropagation()}
              title={
                selectedFilterValues.length > 0
                  ? `${filter.title ?? "Filter"}: ${filterLabel}`
                  : filter.title ?? "Filter"
              }
              aria-label={
                selectedFilterValues.length > 0
                  ? `${filter.title ?? "Filter"} active: ${filterLabel}`
                  : filter.title ?? "Filter"
              }
            >
              <Icon type="Filter" className="size-3.5" />
              {selectedFilterValues.length > 0 && (
                <span className="absolute inset-0 z-10 inline-flex items-center justify-center pointer-events-none">
                  <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-700 px-1 text-[10px] font-semibold leading-none text-white shadow-sm">
                    {selectedFilterValues.length}
                  </span>
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56"
            onClick={(event) => event.stopPropagation()}
          >
            <DropdownMenuLabel>
              {filter.title ?? "Filter"}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {filter.options.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={selectedFilterValues.includes(option.value)}
                onCheckedChange={() => toggleFilterValue(option.value)}
                onSelect={(event) => event.preventDefault()}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
            {selectedFilterValues.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  type="button"
                  className="w-full px-2 py-1.5 text-left text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  onClick={(event) => {
                    event.stopPropagation();
                    header.column.setFilterValue(undefined);
                  }}
                >
                  Clear filter
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </th>
  );
}
